import React, { useState } from "react";
import {
	Box,
	Button,
	Paper,
	Typography,
	LinearProgress,
} from "@mui/material";
import {
	CloudUpload as CloudUploadIcon,
	CheckCircle as CheckCircleIcon,
	Download as DownloadIcon,
} from "@mui/icons-material";
import { uploadFile } from "./services/api";

function FileUpload({ onFileUploaded, onError, loading, setLoading }) {
	const [file, setFile] = useState(null);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [uploaded, setUploaded] = useState(false);

	const validateFile = (f) => {
		const validExtensions = [".csv"];
		const fileExtension = "." + f.name.split(".").pop().toLowerCase();
		if (!validExtensions.includes(fileExtension)) {
			onError("Please upload a CSV file only (.csv)");
			return false;
		}
		return true;
	};

	const handleFileSelect = (event) => {
		const selectedFile = event.target.files[0];
		if (selectedFile && validateFile(selectedFile)) {
			setFile(selectedFile);
			setUploaded(false);
			onError("");
		}
	};

	const handleDrop = (event) => {
		event.preventDefault();
		const droppedFile = event.dataTransfer.files[0];
		if (droppedFile && validateFile(droppedFile)) {
			setFile(droppedFile);
			setUploaded(false);
			onError("");
		}
	};

	const handleDragOver = (event) => {
		event.preventDefault();
	};

	const handleDownloadSample = () => {
		const sampleData = `BARCODE,STORE_NAME,ZONE,QTY
8907279921659,Mall Of Ranchi,EAST,2
8907279921659,PMC-BLR,SOUTH,2
8907279921659,RR-Dehradun-Highstreet,NORTH,2
8907279921659,RR-JOI JAIPUR,NORTH,2`;
		const blob = new Blob([sampleData], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = "sample_format.csv";
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(link.href);
	};

	const handleUpload = async () => {
		if (!file) {
			onError("Please select a file first");
			return;
		}

		const formData = new FormData();
		formData.append("file", file);

		try {
			setLoading(true);
			setUploadProgress(0);
			onError("");

			const response = await uploadFile(formData, (progressEvent) => {
				if (progressEvent.total) {
					const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
					setUploadProgress(pct);
				}
			});

			if (response.data?.success) {
				setUploaded(true);
				onFileUploaded(response.data);
			} else {
				onError(response.data?.error || "Upload failed");
			}
		} catch (error) {
			onError(
				error.response?.data?.error ||
					error.message ||
					"Error uploading file"
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box>
			<Paper
				variant="outlined"
				sx={{
					p: 2,
					mb: 3,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Box>
					<Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
						CSV File Format
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Download sample CSV file with required format
					</Typography>
				</Box>
				<Button
					variant="outlined"
					startIcon={<DownloadIcon />}
					onClick={handleDownloadSample}
				>
					Download Sample
				</Button>
			</Paper>

			<Paper
				variant="outlined"
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				sx={{
					p: 4,
					textAlign: "center",
					border: "2px dashed",
					borderColor: "primary.main",
					borderRadius: 2,
					cursor: "pointer",
					"&:hover": {
						borderColor: "primary.dark",
						bgcolor: "action.hover",
					},
					mb: 3,
				}}
			>
				<CloudUploadIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
				<Typography variant="h6" gutterBottom>
					{file ? file.name : "Click to upload or drag and drop"}
				</Typography>
				<Typography variant="body2" color="text.secondary" gutterBottom>
					CSV files only (.csv)
				</Typography>
				<input
					type="file"
					accept=".csv"
					onChange={handleFileSelect}
					style={{ display: "none" }}
					id="wh-allocation-file-upload"
				/>
				<label htmlFor="wh-allocation-file-upload">
					<Button variant="outlined" component="span" sx={{ mt: 2 }} disabled={loading}>
						Select File
					</Button>
				</label>
			</Paper>

			{file && (
				<Box>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
					</Typography>
				{uploadProgress > 0 && !uploaded && (
					<LinearProgress
						variant="determinate"
						value={uploadProgress}
						sx={{ mt: 1, mb: 2 }}
					/>
				)}
					{uploaded && (
						<Box display="flex" alignItems="center" gap={1} sx={{ color: "success.main" }} mb={2}>
							<CheckCircleIcon />
							<Typography variant="body2">File uploaded successfully!</Typography>
						</Box>
					)}
					<Button
						variant="contained"
						onClick={handleUpload}
						disabled={loading || uploaded}
						fullWidth
						sx={{ mt: 2 }}
					>
						{loading ? "Uploading..." : uploaded ? "Uploaded" : "Upload File"}
					</Button>
				</Box>
			)}
		</Box>
	);
}

export default FileUpload;
